import React, { useState, useEffect, useMemo } from "react";
import { formatMaidTips } from "../../../controllers/reports";
import { Button, Col, Row } from "react-bootstrap";
import { Alert, DatePicker, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleExclamation,
  faDownload,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import moment from "moment";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Box } from "@mui/material";
import usePermissions from "../../../hook/userPermission";

const { RangePicker } = DatePicker;

const DetailedReports = ({ toDownload, isAdmin, maids, type }) => {
  const { hasPermission } = usePermissions();
  const [dateRange, setDateRange] = useState([null, null]);
  const [paymentId, setPaymentId] = useState("");
  const [stripeAccount, setStripeAccount] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [filteredMaidListData, setFilteredMaidListData] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  const allTips = useMemo(
    () =>
      formatMaidTips(maids).sort(
        (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()
      ),
    [maids]
  );

  const handleFilter = () => {
    if (!allTips || allTips.length === 0) {
      setFilteredMaidListData([]);
      setNoResults(true);
      return;
    }

    let startDate = null;
    let endDate = null;

    if (dateRange?.[0] && dateRange?.[1]) {
      startDate = new Date(dateRange[0]);
      endDate = new Date(dateRange[1]);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    }

    const filteredList = allTips.filter((tip) => {
      /* SERVICE NAME FILTER */
      if (
        serviceName &&
        !tip.serviceName?.toLowerCase().includes(serviceName.toLowerCase())
      ) {
        return false;
      }

      /* PAYMENT ID FILTER */
      if (
        paymentId &&
        !tip.paymentId?.toLowerCase().includes(paymentId.toLowerCase().trim())
      ) {
        return false;
      }

      /* STRIPE ACCOUNT FILTER */
      if (
        stripeAccount &&
        !tip.account?.toLowerCase().includes(stripeAccount.toLowerCase())
      ) {
        return false;
      }

      /* DATE RANGE FILTER */
      if (startDate && endDate) {
        const tipDate = new Date(tip.date);

        if (isNaN(tipDate.getTime())) {
          return false;
        }

        if (tipDate < startDate || tipDate > endDate) {
          return false;
        }
      }

      return true;
    });

    setFilteredMaidListData(filteredList);
    setNoResults(filteredList.length === 0);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };
  const handleDownloadExcel = () => {
    const dataToExport =
      filteredMaidListData.length > 0 &&
      (paymentId ||
        serviceName ||
        stripeAccount ||
        (dateRange && dateRange[0] && dateRange[1]))
        ? filteredMaidListData
        : allTips;

    const data = dataToExport.map((transaction) => ({
      "Employ ID": transaction.employId,
      "Payment ID": transaction.paymentId,
      Date: moment(transaction.date).format("MM/DD/YYYY"),
      Name: transaction.maidName,
      "Service Name": transaction.serviceName,
      Tip: `$${transaction.amount}`,
      "Stripe Account": transaction.account,
      Review: transaction.review[0],
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Detailed Report");
    XLSX.writeFile(workbook, "detailed-report.xlsx");
  };

  const handleDateRangeChange = (dates) => setDateRange(dates);
  const handlePaymentIdFilter = (e) => setPaymentId(e.target.value);
  const handleStripeAccountFilter = (e) => setStripeAccount(e.target.value);
  const handleServiceNameFilter = (e) => setServiceName(e.target.value);

  const hasFilters =
    Boolean(paymentId?.trim()) ||
    Boolean(serviceName?.trim()) ||
    Boolean(stripeAccount?.trim()) ||
    Boolean(dateRange?.[0] && dateRange?.[1]);

  // useEffect(() => {
  //   handleFilter();
  // }, [serviceName, paymentId, stripeAccount, dateRange]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        Cell: ({ cell }) => moment(cell.getValue()).format("MM/DD/YYYY"),
        size: 100,
      },
      {
        accessorKey: "employId",
        header: "ID",
        size: 80,
      },
      {
        accessorKey: "maidName",
        header: "Name",
        size: 150,
      },
      {
        accessorKey: "serviceName",
        header: "@",
        size: 120,
      },
      {
        accessorKey: "amount",
        header: "Tip",
        Cell: ({ cell }) => `$${cell.getValue()}`,
        size: 80,
      },
      {
        accessorKey: "review",
        header: "Review",
        size: 200,
      },
      {
        accessorKey: "paymentId",
        header: "Payment ID",
        size: 250,
      },
      {
        accessorKey: "account",
        header: "Stripe Account",
        size: 150,
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data:
      hasFilters && filteredMaidListData.length
        ? filteredMaidListData
        : allTips,
    initialState: {
      density: "compact",
    },
    enableStickyHeader: true,
    enableColumnFilters: false,
    enableColumnOrdering: false,
    enableSorting: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        color: "#333",
      },
    },
  });

  return (
    <>
      <Row className="px-3 mb-5">
        {noResults && allTips?.length > 0 && (
          <Alert
            message="No results found"
            type="error"
            showIcon
            closable
            onClose={() => setNoResults(false)}
          />
        )}
        <Row className="my-4 align-items-center justify-content-center">
          <Col xs="auto">
            <RangePicker
              className="py-2"
              value={dateRange}
              onChange={handleDateRangeChange}
              placeholder={["Start Date", "End Date"]}
            />
          </Col>
          <Col xs={3}>
            <Input
              className="py-2"
              placeholder="Payment ID"
              value={paymentId}
              onChange={handlePaymentIdFilter}
              allowClear
            />
          </Col>
          <Col xs={2}>
            <Input
              className="py-2"
              placeholder="Stripe Account"
              value={stripeAccount}
              onChange={handleStripeAccountFilter}
              allowClear
            />
          </Col>
          <Col xs={2}>
            <Input
              className="py-2"
              placeholder="Service Name"
              value={serviceName}
              onChange={handleServiceNameFilter}
              allowClear
            />
          </Col>
          <Col xs="auto">
            <Button
              variant="outline-secondary"
              className="px-3 mx-1"
              onClick={handleFilter}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </Button>
            {hasPermission("toDownload") && (
              <Button
                variant="outline-secondary"
                className="px-3 mx-1"
                onClick={handleDownloadExcel}
              >
                <FontAwesomeIcon icon={faDownload} />
              </Button>
            )}
          </Col>
        </Row>
        <Col className="p-2 border border-1 rounded-1">
          <MaterialReactTable table={table} />
        </Col>
      </Row>
    </>
  );
};

export default DetailedReports;
